import Keycloak, {
  type KeycloakConfig,
  type KeycloakInitOptions,
} from "keycloak-js";
import type { App } from "vue";
import { config as appConfig } from "@/config/config";

let keycloak: Keycloak;

const LOGIN_LOCATION_PREFIX = "/login/location=";

/**
 * Builds the redirect URI used to return the user to their original application
 * destination after authentication.
 *
 * The current pathname and query/search parameters are wrapped in a `location`
 * parameter, `encodeURIComponent`-encoded so the result is always a single
 * path segment with no raw `/`, `?`, `&`, or `#` in it. That single-segment
 * guarantee is what lets `/login/:location?` (see router/index.ts) match it.
 *
 * If we're already sitting on a wrapped `/login/location=...` redirect target
 * (Keycloak re-entering the auth flow before the app ever navigated away -
 * e.g. an overnight silent-refresh failure re-triggering login), the original
 * destination is decoded back out and reused as-is rather than wrapped again.
 * The previous version of this function tried to detect that case by
 * matching `&location=` against `window.location.pathname`, which can never
 * match (pathname never contains `&`) - so it always fell through to
 * re-wrapping the already-wrapped value, and repeated re-logins (as happens
 * overnight) produced a runaway nested location that no route could match,
 * landing on the 404 page instead of back in the app.
 */
function getRedirectUri(): string {
  const currentPath = window.location.pathname;

  const originalDestination = currentPath.startsWith(LOGIN_LOCATION_PREFIX)
    ? decodeURIComponent(currentPath.slice(LOGIN_LOCATION_PREFIX.length))
    : currentPath + window.location.search;

  return (
    window.location.origin +
    LOGIN_LOCATION_PREFIX +
    encodeURIComponent(originalDestination)
  );
}

const initOptions: KeycloakInitOptions = {
  onLoad: "login-required",
  redirectUri: getRedirectUri(),
  checkLoginIframe: false,
  scope: 'essentials',
};

async function initialiseAuth(): Promise<boolean> {
  // Read config here rather than at module load, so it reflects whatever
  // loadRuntimeConfig() resolved by the time main.ts calls this.
  const keycloakConfig: KeycloakConfig = {
    url: appConfig.authUrl,
    realm: appConfig.authRealm,
    clientId: appConfig.authClientId,
  };

  keycloak = new Keycloak(keycloakConfig);

  const authed = await keycloak.init(initOptions);

  // Automatic token refresh
  setInterval(() => {
    if (keycloak.authenticated && keycloak.refreshToken) {
      keycloak.updateToken(60).catch(() => {
        console.warn("Token refresh failed");
      });
    }
  }, 60000);

  return authed;
}

const authPlugin = {
  install: (app: App) => {
    app.provide("keycloak", keycloak);
    app.config.globalProperties.$keycloak = keycloak;
  },
};

export { authPlugin, initialiseAuth, keycloak };

